/**
 * Maps game slugs to lazy-loaded game modules. Each game exports a `mount()`
 * function that takes a container element and mount options.
 *
 * Adding a new game: import its package here and add a slug → loader entry.
 */

import type { GameModule } from "@stratacademy/game-bridge";

type GameLoader = () => Promise<GameModule>;

const registry: Record<string, GameLoader> = {
  "blitz-tanks": () => import("@stratacademy/game-blitz-tanks") as Promise<GameModule>,
  "math-slice-dojo": () => import("@stratacademy/game-math-slice-dojo") as Promise<GameModule>,
  "fast-math-defense": () => import("@stratacademy/game-fast-math-defense") as Promise<GameModule>,
  // Retired (TEX-2696): archived in the DB + removed from GAME_CATALOG so they're
  // never browsed/recommended, but the loader stays so a saved /play link or a
  // pre-ingest catalog row still resolves — archived plays are blocked by the API
  // gate (graceful "couldn't start" path), not a null-loader failure.
  "dungeon-of-digits": () => import("@stratacademy/game-dungeon-of-digits") as Promise<GameModule>,
  "fast-math-battleship": () =>
    import("@stratacademy/game-fast-math-battleship") as Promise<GameModule>,
  "math-crush": () => import("@stratacademy/game-math-crush") as Promise<GameModule>,
  "blaster-gate": () => import("@stratacademy/game-blaster-gate") as Promise<GameModule>,
  "smash-math-badminton": () =>
    import("@stratacademy/game-smash-math-badminton") as Promise<GameModule>,
  "sports-raiders": () => import("@stratacademy/game-sports-raiders") as Promise<GameModule>,
  "math-strikers": () => import("@stratacademy/game-math-strikers") as Promise<GameModule>,
  boxing: () => import("@stratacademy/game-boxing") as Promise<GameModule>,
  "karate-board-break": () =>
    import("@stratacademy/game-karate-board-break") as Promise<GameModule>,
  "horseshoe-toss": () => import("@stratacademy/game-horseshoe-toss") as Promise<GameModule>,
  "knight-charge": () => import("@stratacademy/game-knight-charge") as Promise<GameModule>,
  "wizard-duel": () => import("@stratacademy/game-wizard-duel") as Promise<GameModule>,
  "summit-scramble": () => import("@stratacademy/game-summit-scramble") as Promise<GameModule>,
  "star-charter": () => import("@stratacademy/game-star-charter") as Promise<GameModule>,
  // crowd-rush and dungeon-duel moved inside Numeria (games/numeria/src/minigames/)
  // — they launch from overworld portals now, not from the match catalog.
  "solar-assembly": () => import("@stratacademy/game-solar-assembly") as Promise<GameModule>,
  "dragon-tamer": () => import("@stratacademy/game-dragon-tamer") as Promise<GameModule>,
  "deep-dig": () => import("@stratacademy/game-deep-dig") as Promise<GameModule>,
  "cannon-cove": () => import("@stratacademy/game-cannon-cove") as Promise<GameModule>,
  "minecart-junction": () => import("@stratacademy/game-minecart-junction") as Promise<GameModule>,
  "buzzer-beater": () => import("@stratacademy/game-buzzer-beater") as Promise<GameModule>,
  "batting-cage": () => import("@stratacademy/game-batting-cage") as Promise<GameModule>,
  "sprout-fields": () => import("@stratacademy/game-sprout-fields") as Promise<GameModule>,
  "blossom-leap": () => import("@stratacademy/game-blossom-leap") as Promise<GameModule>,
  // aero-academy is intentionally absent: numeria is its only host, so it stays out
  // of GAME_CATALOG and the match catalog (same as zombie-rush / crowd-rush /
  // dungeon-duel). Its loader lives in games/numeria/src/minigames/defs.ts.
  "diamond-rush": () => import("@stratacademy/game-diamond-rush") as Promise<GameModule>,
  whiteout: () => import("@stratacademy/game-whiteout") as Promise<GameModule>,
  "slam-streak": () => import("@stratacademy/game-slam-streak") as Promise<GameModule>,
  "bullseye-blitz": () => import("@stratacademy/game-bullseye-blitz") as Promise<GameModule>,
  "grand-prix": () => import("@stratacademy/game-grand-prix") as Promise<GameModule>,
  "sky-squadron": () => import("@stratacademy/game-sky-squadron") as Promise<GameModule>,
  "treasure-dig": () => import("@stratacademy/game-treasure-dig") as Promise<GameModule>,
  "orchard-harvest": () => import("@stratacademy/game-orchard-harvest") as Promise<GameModule>,
  "path-fix-race": () => import("@stratacademy/game-path-fix-race") as Promise<GameModule>,
  "shooting-gallery": () => import("@stratacademy/game-shooting-gallery") as Promise<GameModule>,
  "tower-stackers": () => import("@stratacademy/game-tower-stackers") as Promise<GameModule>,
  "snapshot-safari": () => import("@stratacademy/game-snapshot-safari") as Promise<GameModule>,
  "fizz-lab": () => import("@stratacademy/game-fizz-lab") as Promise<GameModule>,
  "flag-frontier": () => import("@stratacademy/game-flag-frontier") as Promise<GameModule>,
  "dino-parade": () => import("@stratacademy/game-dino-parade") as Promise<GameModule>,
  "garden-party": () => import("@stratacademy/game-garden-party") as Promise<GameModule>,
  "scoop-squad": () => import("@stratacademy/game-scoop-squad") as Promise<GameModule>,
  "forge-frenzy": () => import("@stratacademy/game-forge-frenzy") as Promise<GameModule>,
  "bolt-busters": () => import("@stratacademy/game-bolt-busters") as Promise<GameModule>,
  "hurdle-run": () => import("@stratacademy/game-hurdle-run") as Promise<GameModule>,
  "frog-jump": () => import("@stratacademy/game-frog-jump") as Promise<GameModule>,
  "treasure-rush": () => import("@stratacademy/game-treasure-rush") as Promise<GameModule>,
  "farm-grow": () => import("@stratacademy/game-farm-grow") as Promise<GameModule>,
  "tightrope-walkers": () => import("@stratacademy/game-tightrope-walkers") as Promise<GameModule>,
  "rodeo-bull-riding": () => import("@stratacademy/game-rodeo-bull-riding") as Promise<GameModule>,
  "toy-box-claw-grabber": () =>
    import("@stratacademy/game-toy-box-claw-grabber") as Promise<GameModule>,
  "balloon-pop-math": () => import("@stratacademy/game-balloon-pop-math") as Promise<GameModule>,
  "treasure-cart-dash": () =>
    import("@stratacademy/game-treasure-cart-dash") as Promise<GameModule>,
  "beam-team": () => import("@stratacademy/game-beam-team") as Promise<GameModule>,
  "frosty-catch": () => import("@stratacademy/game-frosty-catch") as Promise<GameModule>,
  "firefly-jar": () => import("@stratacademy/game-firefly-jar") as Promise<GameModule>,
  "snowman-builder": () => import("@stratacademy/game-snowman-builder") as Promise<GameModule>,
  "bridge-builder": () => import("@stratacademy/game-bridge-builder") as Promise<GameModule>,
  "watercolor-meadow": () => import("@stratacademy/game-watercolor-meadow") as Promise<GameModule>,
  "sub-sprint": () => import("@stratacademy/game-sub-sprint") as Promise<GameModule>,
  "up-and-away": () => import("@stratacademy/game-up-and-away") as Promise<GameModule>,
  "cannon-clash": () => import("@stratacademy/game-cannon-clash") as Promise<GameModule>,
  "timber-trail": () => import("@stratacademy/game-timber-trail") as Promise<GameModule>,
  "fish-frenzy": () => import("@stratacademy/game-fish-frenzy") as Promise<GameModule>,
  "kitty-cannons": () => import("@stratacademy/game-kitty-cannons") as Promise<GameModule>,
  "tug-of-car": () => import("@stratacademy/game-tug-of-car") as Promise<GameModule>,
  "diagnostic-math-simple": () =>
    import("../games/diagnostic-math-simple/index.js") as Promise<GameModule>,
};

/** Load a game module by slug. Returns null if the slug is not registered. */
export function loadGame(slug: string): ReturnType<GameLoader> | null {
  const loader = registry[slug];
  return loader ? loader() : null;
}

/** All mountable game slugs, sorted. Used by the admin question-preview picker. */
export const registeredGameSlugs: string[] = Object.keys(registry).sort();
